import { useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, X, Loader2, Check, Sparkles } from "lucide-react";

export const CARD_GROUPS = [
  { network: "Visa", cards: ["Visa Electron", "Visa Classic", "Visa Premier", "Visa Signature", "Visa Infinite"] },
  { network: "Mastercard", cards: ["Mastercard Standard", "Gold Mastercard", "Platinum Mastercard", "World Elite Mastercard"] },
  { network: "American Express", cards: ["American Express Blue", "American Express Green", "American Express Gold", "American Express Platinum", "American Express Centurion"] },
  { network: "Air France KLM – American Express", cards: ["Air France KLM Amex Silver", "Air France KLM Amex Gold", "Air France KLM Amex Platinum"] },
  { network: "Néobanques & en ligne", cards: ["Revolut Standard", "Revolut Plus", "Revolut Premium", "Revolut Metal", "Revolut Ultra", "N26 Standard", "N26 You", "N26 Metal", "Boursorama Ultim", "Boursorama Metal", "Fortuneo Gold Mastercard", "Fortuneo World Elite Mastercard", "Hello bank Hello Prime", "Wise", "Lydia"] },
];

export default function CardPicker({ open, onClose, onAdded }) {
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);
  const [current, setCurrent] = useState("");
  const [custom, setCustom] = useState("");
  const [notListed, setNotListed] = useState("");

  if (!open) return null;

  const ALL_PREDEFINED = CARD_GROUPS.flatMap((g) => g.cards);
  const customSelected = selected.filter((c) => !ALL_PREDEFINED.includes(c));

  const toggle = (card) => {
    setSelected((s) => (s.includes(card) ? s.filter((c) => c !== card) : [...s, card]));
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!selected.includes(v)) setSelected((s) => [...s, v]);
    setCustom("");
  };

  const addNotListed = async () => {
    const v = notListed.trim();
    if (!v) return;
    setAdding(true);
    try {
      await api.post("/contracts/placeholder", { card: v });
      toast.success(`« ${v} » ajoutée à compléter — pensez à importer sa notice`);
      setNotListed("");
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const analyze = async () => {
    if (!selected.length) return;
    setAdding(true);
    const addedContracts = [];
    for (const card of selected) {
      setCurrent(card);
      try {
        const { data } = await api.post("/contracts/card", { card }); // eslint-disable-line no-await-in-loop
        addedContracts.push(data);
        toast.success(`Garanties de « ${card} » ajoutées`);
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail) || `Échec pour « ${card} »`);
      }
    }
    setAdding(false);
    setCurrent("");
    setSelected([]);
    if (addedContracts.length) onAdded?.(addedContracts);
    onClose?.();
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm" onClick={adding ? undefined : onClose} data-testid="card-picker-modal">
      <div className="w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl border border-border bg-card shadow-xl animate-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
              <CreditCard className="w-4 h-4" />
            </div>
            <div>
              <p className="font-heading font-bold leading-none">Quelles cartes bancaires avez-vous ?</p>
              <p className="text-xs text-muted-foreground mt-1">L'IA retrouve automatiquement les assurances incluses</p>
            </div>
          </div>
          <button onClick={onClose} disabled={adding} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-muted transition disabled:opacity-40" data-testid="card-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-5">
          {CARD_GROUPS.map((g) => (
            <div key={g.network}>
              <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">{g.network}</p>
              <div className="flex flex-wrap gap-2">
                {g.cards.map((card) => {
                  const active = selected.includes(card);
                  return (
                    <button
                      key={card}
                      data-testid={`card-option-${card.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => toggle(card)}
                      className={`flex items-center gap-2 h-9 px-3.5 rounded-full border text-sm font-medium transition ${
                        active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"
                      }`}
                    >
                      {active && <Check className="w-3.5 h-3.5" />}
                      {card}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">Autre carte (non listée)</p>
            <div className="flex gap-2">
              <input
                data-testid="card-custom-input"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                placeholder="Nom exact de votre carte (ex: Visa Premier Boursorama)"
                className="flex-1 h-9 px-3 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
              />
              <button data-testid="card-custom-add" onClick={addCustom} className="h-9 px-3.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition shrink-0">
                Ajouter
              </button>
            </div>
            {customSelected.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2.5">
                {customSelected.map((c) => (
                  <span key={c} data-testid={`card-custom-chip-${c.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-primary bg-primary text-primary-foreground text-sm font-medium">
                    {c}
                    <button onClick={() => toggle(c)} className="opacity-80 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">Votre carte n'est pas citée ?</p>
            <div className="flex gap-2">
              <input
                data-testid="card-notlisted-input"
                value={notListed}
                onChange={(e) => setNotListed(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNotListed(); } }}
                placeholder="Nom de votre carte (ex: Carte Gold CIC)"
                className="flex-1 h-9 px-3 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
              />
              <button
                data-testid="card-notlisted-add"
                onClick={addNotListed}
                disabled={adding || !notListed.trim()}
                className="h-9 px-3.5 rounded-xl border border-amber-500/60 text-amber-700 dark:text-amber-300 text-sm font-medium hover:bg-amber-500/10 transition shrink-0 disabled:opacity-50"
              >
                À compléter
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              On l'ajoute à « Mes contrats » avec un rappel d'<span className="font-medium text-foreground">importer vous-même</span> sa notice. Aucune garantie n'est devinée.
            </p>
          </div>

          <div className="rounded-xl bg-accent border border-ochre/25 p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 shrink-0 text-ochre mt-0.5" />
            <p className="text-sm text-accent-foreground">
              Les garanties d'une carte (location de voiture, voyage, achats…) varient selon la banque. Iacover ajoute les garanties <span className="font-semibold">standard indicatives</span> — vérifiez la notice de votre carte.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground shrink-0">{selected.length} sélectionnée(s)</span>
          <button
            data-testid="card-analyze-btn"
            onClick={analyze}
            disabled={adding || !selected.length}
            className="flex items-center justify-center gap-2 h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition min-w-0 flex-1 sm:flex-none overflow-hidden"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Sparkles className="w-4 h-4 shrink-0" />}
            <span className="truncate">{adding ? `Analyse de « ${current} »…` : `Analyser ${selected.length || ""} carte${selected.length > 1 ? "s" : ""}`.trim()}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
